import {
  clearIndexedDbPersistence,
  disableNetwork,
  enableNetwork,
  terminate,
  waitForPendingWrites,
} from 'firebase/firestore';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import {
  clearDatabase,
  getDatabase,
  getExistingDatabase,
} from './get-database';

export class FirestoreNetworkManager {
  idleTimer: number | null;
  sleepCheckInterval: number | null;
  lastTick: number;
  isSyncing: boolean;
  networkEnabled: boolean;
  networkProcessing: boolean;
  wantsToEnableWhileDisabling: boolean;

  BACKGROUND_TIMEOUT_MS: number;
  SLEEP_THRESHOLD_MS: number;
  SYNC_TIMEOUT_MS: number;

  handleOnline: () => Promise<void>;
  handleError: (event: ErrorEvent) => void;

  constructor() {
    this.idleTimer = null;
    this.sleepCheckInterval = null;
    this.lastTick = Date.now();
    this.isSyncing = false;
    this.networkEnabled = true; // Assume network starts enabled
    this.networkProcessing = false;
    this.wantsToEnableWhileDisabling = false;

    // Configuration
    this.BACKGROUND_TIMEOUT_MS = 60 * 1000;
    this.SLEEP_THRESHOLD_MS = 10 * 1000;
    this.SYNC_TIMEOUT_MS = 10 * 1000;

    this.handleVisibility = this.handleVisibility.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
    this.handleOnline = () => this.connect('Online Event');
    this.handleError = (event) => this.handleGlobalError(event);

    this.init();
  }

  init() {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- logging network
      console.log('[Network] 🛡️ Firestore Network Manager Initialized');
    }

    // electron-specific listeners
    window.addEventListener('blur', () => this.handleWindowBlur());
    window.addEventListener('focus', () => this.handleWindowFocus());
    // web specific listener
    document.addEventListener('visibilitychange', this.handleVisibility);

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('error', this.handleError);

    this.sleepCheckInterval = window.setInterval(
      () => this.checkSystemSleep(),
      2000,
    );
  }

  cleanup() {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- logging network
      console.log('[Network] 🧹 Cleaning up listeners');
    }
    document.removeEventListener('focus', this.handleWindowFocus);
    document.removeEventListener('focus', this.handleWindowBlur);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('error', this.handleError);

    if (this.sleepCheckInterval) {
      clearInterval(this.sleepCheckInterval);
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  handleWindowBlur() {
    // Only trigger if we aren't ALREADY syncing or idle
    if (!this.idleTimer && !this.isSyncing) {
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- logging network
        console.log('📉 Window blurred (backgrounding)...');
      }
      // Manually trigger the "Hidden" logic
      this.handleVisibilityChange(true);
    }
  }

  // 2. When user clicks back -> Treat as "Visible"
  handleWindowFocus() {
    // Manually trigger the "Visible" logic
    this.handleVisibilityChange(false);
  }

  handleVisibility() {
    this.handleVisibilityChange(document.visibilityState === 'hidden');
  }

  handleVisibilityChange(isHidden: boolean) {
    if (isHidden) {
      // --- GOING BACKGROUND ---
      if (this.isSyncing) {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.log('⏳ Backgrounded, but syncing... keeping alive.');
        }
        return;
      }

      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }

      this.idleTimer = window.setTimeout(() => {
        this.idleTimer = null;
        this.disconnect('Background Idle > 60s');
      }, this.BACKGROUND_TIMEOUT_MS);
    } else {
      // --- COMING FOREGROUND ---
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
        this.idleTimer = null;

        // ✅ THIS is the log you were looking for
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.log('⚡ User returned. Connection kept alive.');
        }
      } else {
        // If the timer already fired (disconnected), we reconnect now
        this.connect('Foreground');
      }
    }
  }

  async connect(reason: string) {
    if (!navigator.onLine) {
      return;
    }

    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- logging network
      console.log(`[Network] 🟢 Enabling: ${reason}`);
      // eslint-disable-next-line no-console -- logging network
      console.time('📡 Enable Network (Background)');
      // eslint-disable-next-line no-console -- logging network
      console.log(
        !this.networkEnabled
          ? '⏸️➡️▶️ Enabling Network'
          : '▶️ network already enabled, not enabling',
      );
      // eslint-disable-next-line no-console -- logging network
      console.log(
        this.networkProcessing
          ? 'enabling/disabling network already'
          : 'not enabling or disabling network yet',
      );
    }

    if (!this.networkEnabled && !this.networkProcessing) {
      this.wantsToEnableWhileDisabling = false;

      try {
        this.networkProcessing = true;
        // 1. FIRE AND FORGET (Optimistic)
        // We do not 'await' this, allowing the UI to render cached data immediately.
        enableNetwork(getDatabase())
          .then(() => {
            this.networkEnabled = true;
            this.networkProcessing = false;
            if (!isProductionEnvironment) {
              // eslint-disable-next-line no-console -- logging network
              console.timeEnd('📡 Enable Network (Background)');
            }

            // 2. Once connected, start draining queue
            if (!isProductionEnvironment) {
              // eslint-disable-next-line no-console -- logging network
              console.time('⏳ Queue Drain');
            }
            return this.waitForQueueToDrain();
          })
          .then(() => {
            if (!isProductionEnvironment) {
              // eslint-disable-next-line no-console -- logging network
              console.timeEnd('⏳ Queue Drain');
            }
            return;
          })
          .catch((e) => {
            this.networkEnabled = false;
            this.networkProcessing = false;
            if (!isProductionEnvironment) {
              // eslint-disable-next-line no-console -- logging network
              console.warn('[Network] Background Connection Error:', e);
            }
            return;
          });

        // Code continues immediately here...
      } catch (e) {
        this.networkProcessing = false;
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.warn('[Network] Connect warning:', e);
        }
      }
      return;
    }

    if (this.networkProcessing) {
      this.wantsToEnableWhileDisabling = true;
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- logging network
        console.log('[Network] ⏳ Connection queued (busy)');
      }
    }
  }

  async disconnect(reason: string) {
    const database = getExistingDatabase();
    // A tab without a client has nothing to hand over, and a client started here would be an offline primary for every tab.
    if (!database) {
      return;
    }

    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- logging network
      console.log(`[Network] 🔴 Disabling: ${reason}`);
      // eslint-disable-next-line no-console -- logging network
      console.log(
        this.networkEnabled
          ? '▶️➡️⏸️ Disabling Network'
          : '⏸️network disabled, not enabling',
      );
      // eslint-disable-next-line no-console -- logging network
      console.log(
        this.networkProcessing
          ? 'enabling/disabling network already'
          : 'not enabling or disabling network yet',
      );
    }

    if (!this.networkProcessing && this.networkEnabled) {
      try {
        this.networkProcessing = true;
        await disableNetwork(database);
        this.networkEnabled = false;
      } catch (e) {
        // Ignore errors
      } finally {
        this.networkProcessing = false;
      }

      if (this.wantsToEnableWhileDisabling) {
        this.wantsToEnableWhileDisabling = false;
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.log('[Network] 🔄 Processing queued connection...');
        }
        setTimeout(() => this.connect('Pending Queue'), 0);
      }
    }
  }

  async waitForQueueToDrain() {
    this.isSyncing = true;

    try {
      await Promise.race([
        waitForPendingWrites(getDatabase()),
        new Promise((_resolve, reject) =>
          window.setTimeout(() => reject('Sync Timeout'), this.SYNC_TIMEOUT_MS),
        ),
      ]);
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- logging network
        console.log('✅ Offline Queue flushed.');
      }
    } catch (e) {
      // If it's a timeout, we swallow it to allow the app to proceed
      if (e === 'Sync Timeout') {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.warn('⚠️ Queue flush timed out (proceeding anyway).');
        }
      } else {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.warn('⚠️ Queue flush error:', e);
        }
      }
    } finally {
      this.isSyncing = false;
      // Re-check visibility logic in case user backgrounded while we were waiting
      if (document.visibilityState === 'hidden') {
        this.handleVisibility();
      }
    }
  }

  handleOffline() {
    this.isSyncing = false;
  }

  handleGlobalError(event: ErrorEvent) {
    if (event.message?.includes('refusing to open IndexedDB')) {
      this.handleIndexedDbCorruption();
      return;
    }

    if (event.message?.includes('INTERNAL ASSERTION FAILED')) {
      const reloadKey = 'fs_crash_reload_count';
      const attempts = parseInt(sessionStorage.getItem(reloadKey) || '0');

      if (attempts < 3 && navigator.onLine) {
        sessionStorage.setItem(reloadKey, (attempts + 1).toString());
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.log('🔄 Attempting recovery reload...');
        }
        window.location.reload();
      } else {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- logging network
          console.error(
            '💀 Max reloads reached. Corrupt persistence or fatal error.',
          );
        }
        sessionStorage.removeItem(reloadKey);
      }
    }
  }

  async handleIndexedDbCorruption() {
    const reloadKey = 'fs_idb_corruption_reload';
    const attempts = parseInt(sessionStorage.getItem(reloadKey) || '0');

    if (attempts >= 2) {
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- logging network
        console.error('💀 IndexedDB recovery failed after max attempts.');
      }
      sessionStorage.removeItem(reloadKey);
      return;
    }

    sessionStorage.setItem(reloadKey, (attempts + 1).toString());

    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- logging network
      console.log(
        '🗄️ IndexedDB corruption detected. Clearing persistence and reloading...',
      );
    }

    try {
      const db = getDatabase();
      await terminate(db);
      await clearIndexedDbPersistence(db);
    } catch (e) {
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- logging network
        console.warn('⚠️ Could not clear IndexedDB persistence:', e);
      }
    }

    clearDatabase();
    window.location.reload();
  }

  checkSystemSleep() {
    const now = Date.now();
    const diff = now - this.lastTick;
    this.lastTick = now;

    if (diff > this.SLEEP_THRESHOLD_MS) {
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- logging network
        console.log('💤 System woke from sleep! Refreshing connection...');
      }
      return this.refreshConnection();
    }
  }

  async refreshConnection() {
    // Force a full cycle to clear the "Morning After" socket
    // eslint-disable-next-line no-console -- logging network
    await this.disconnect('Sleep Cycle');
    setTimeout(() => this.connect('Sleep Cycle'), 1000);
  }
}

let manager: FirestoreNetworkManager | undefined;

export const connectionHelper = () => {
  if (!manager) {
    manager = new FirestoreNetworkManager();
  }
  return manager;
};
